import { useState, useEffect, useCallback, useRef } from 'react';

const INACTIVITY_DELAY = 30000; // 30 seconds
const DISMISS_KEY = 'chat_nudge_dismissed';

export default function ChatNudgeBar() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownRef = useRef(false);

  const openWidget = () => {
    const widget = document.querySelector('readdy-agent') as HTMLElement & { open?: () => void };
    if (widget && typeof widget.open === 'function') {
      widget.open();
    } else {
      const btn = document.querySelector('.readdy-agent-button') as HTMLElement;
      if (btn) btn.click();
    }
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, '1');
  };

  const dismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, '1');
  };

  const resetTimer = useCallback(() => {
    if (shownRef.current || dismissed) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!shownRef.current && !sessionStorage.getItem(DISMISS_KEY)) {
        shownRef.current = true;
        setVisible(true);
      }
    }, INACTIVITY_DELAY);
  }, [dismissed]);

  useEffect(() => {
    // Don't show if already dismissed this session
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));

    // Start the initial timer
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resetTimer]);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-24 right-5 z-40 max-w-[320px] w-[calc(100vw-40px)]"
      style={{ animation: 'nudgeSlideIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both' }}
    >
      <style>{`
        @keyframes nudgeSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div className="bg-[#1A3A52] rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(26,58,82,0.35)' }}>
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-[#D97642] to-[#F0A070]"></div>

        <div className="p-4">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5">
              <div className="relative flex-shrink-0">
                <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/20 rounded-full">
                  <i className="ri-chat-3-fill text-[#D97642] text-lg"></i>
                </div>
                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-[#1A3A52]"></span>
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight">Questions? Chat now</p>
                <p className="text-white/50 text-[11px]">Typically replies in seconds</p>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="w-6 h-6 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors cursor-pointer flex-shrink-0 mt-0.5"
              aria-label="Dismiss"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>

          {/* Message bubble */}
          <div className="bg-white/10 rounded-xl px-3.5 py-2.5 mb-3">
            <p className="text-white/90 text-[13px] leading-relaxed">
              Still have questions about rates or coverage? I can compare options for you right now — no forms, no waiting.
            </p>
          </div>

          {/* CTA button */}
          <button
            onClick={openWidget}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#D97642] hover:bg-[#C26638] text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-3-line text-base"></i>
            Start Chat
            <span className="flex items-center gap-1 ml-1">
              <span className="w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse"></span>
              <span className="text-emerald-300 text-[11px] font-semibold">Live</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
